import { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { abonnerUleste, oppdaterUleste, sisteUleste } from '../../lib/ulesteVarsler'

// Tilgangene et varsel kan kreve (server/notifications/varsler.js,
// VARSEL_TILGANGER). Uten noen av dem har brukeren ingen varsler, og bjella
// vises ikke.
const VARSEL_TILGANGER = ['collixl_orders.view', 'customers.view', 'customers.edit', 'innboks.view', 'innboks.jobbsoknader']

// Hvor ofte bjella spør. Et minutt er raskt nok for en ny nettbestilling og
// billig: ett tellende SELECT per åpen fane.
const INTERVALL_MS = 60_000

const synlig = () => typeof document === 'undefined' || document.visibilityState !== 'hidden'

/**
 * Bjella i menyen: antall uleste varsler, og lenke til /varsler. Spør bare
 * når fanen er synlig, og når brukeren bytter side. Tallet deles av alle
 * bjellene og hentes høyst hvert 15. sekund (src/lib/ulesteVarsler.js), så
 * menyen som glir ut og inn ikke gir et kall hver gang.
 */
export default function NotificationBell({ collapsed = false, onNavigate }) {
  const { hasPermission } = useAuth()
  const { pathname } = useLocation()
  const [uleste, setUleste] = useState(sisteUleste)
  const harVarsler = VARSEL_TILGANGER.some(t => hasPermission(t))

  useEffect(() => {
    if (!harVarsler) return undefined
    const avslutt = abonnerUleste(setUleste)
    const hent = () => { if (synlig()) oppdaterUleste() }
    const timer = setInterval(hent, INTERVALL_MS)
    const vedSynlig = () => { if (synlig()) oppdaterUleste() }
    document.addEventListener('visibilitychange', vedSynlig)
    return () => {
      avslutt()
      clearInterval(timer)
      document.removeEventListener('visibilitychange', vedSynlig)
    }
  }, [harVarsler])

  // Egen effekt, så et sidebytte ikke starter intervallet på nytt.
  useEffect(() => {
    if (harVarsler && synlig()) oppdaterUleste()
  }, [harVarsler, pathname])

  if (!harVarsler) return null
  const etikett = uleste > 0 ? `Varsler, ${uleste} uleste` : 'Varsler'

  return (
    <NavLink
      to="/varsler"
      onClick={onNavigate}
      title={etikett}
      aria-label={etikett}
      className={({ isActive }) => `relative ${collapsed ? 'w-full flex justify-center p-1' : 'w-9 h-9 grid place-items-center rounded-lg'} transition-colors ${
        isActive ? 'text-secondary' : 'text-text-muted hover:text-text hover:bg-surface'
      }`}
    >
      <svg className={collapsed ? 'w-5 h-5' : 'w-4 h-4'} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
      </svg>
      {uleste > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-danger text-white text-[10px] font-semibold leading-4 text-center" aria-hidden="true">
          {uleste > 99 ? '99+' : uleste}
        </span>
      )}
    </NavLink>
  )
}
